import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import Navbar from './Navbar';
import { 
    FaUpload, 
    FaFileCsv, 
    FaCheckCircle, 
    FaInfoCircle, 
    FaQuestionCircle, 
    FaSpinner, 
    FaFileAlt, 
    FaCloudUploadAlt,
    FaDownload,
    FaExclamationTriangle,
    FaTimes,
    FaChartBar,
    FaLightbulb,
    FaFire,
    FaBolt
} from 'react-icons/fa';
import { 
    MdDashboard, 
    MdUploadFile, 
    MdCategory,
    MdDescription,
    MdCloudUpload,
    MdFileUpload
} from 'react-icons/md';
import { 
    IoDocument, 
    IoCheckmarkCircle, 
    IoWarning 
} from 'react-icons/io5';
import {
    HiSparkles,
    HiLightningBolt,
    HiDocumentText
} from 'react-icons/hi';

const Question = () => {
    const [file, setFile] = useState(null);
    const [type, setType] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [dragOver, setDragOver] = useState(false);

    const handleFileChange = (selectedFile) => {
        if (selectedFile) {
            // Check if file is CSV
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                toast.error('Please upload a CSV file only');
                return;
            }
            
            // Check file size (5MB limit)
            if (selectedFile.size > 5 * 1024 * 1024) {
                toast.error('File size must be less than 5MB');
                return;
            }
            
            setFile(selectedFile);
            
            // Create preview URL for CSV file
            const reader = new FileReader();
            reader.onload = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsText(selectedFile);
        }
    };

    const handleInputChange = (e) => {
        const selectedFile = e.target.files[0];
        handleFileChange(selectedFile);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const droppedFile = e.dataTransfer.files[0];
        handleFileChange(droppedFile);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!file) {
            toast.error('Please select a CSV file');
            return;
        }
        if (!type) {
            toast.error('Please select question type');
            return;
        }

        setLoading(true);
        setUploadProgress(0);

        try {
            // Simulate progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return prev;
                    }
                    return prev + 10;
                });
            }, 200);

            const response = await Calling.uploadQuestionCSV(file, type);
            
            clearInterval(progressInterval);
            setUploadProgress(100);

            if (response.status) {
                toast.success('Questions uploaded successfully');
                // Reset form
                setFile(null);
                setType('');
                setPreviewUrl(null);
                setUploadProgress(0);
                // Reset file input
                const fileInput = document.getElementById('file');
                if (fileInput) fileInput.value = '';
            } else {
                toast.error(response.message || 'Upload failed');
            }
        } catch (error) {
            toast.error('Something went wrong');
            console.error('Upload error:', error);
        } finally {
            setLoading(false);
        }
    };

    const removeFile = () => {
        setFile(null);
        setPreviewUrl(null);
        const fileInput = document.getElementById('file');
        if (fileInput) fileInput.value = '';
    };

    const getDifficultyIcon = (difficulty) => {
        switch (difficulty) {
            case 'easy': return <FaLightbulb className="w-5 h-5" />;
            case 'medium': return <FaChartBar className="w-5 h-5" />;
            case 'hard': return <FaFire className="w-5 h-5" />;
            default: return <FaQuestionCircle className="w-5 h-5" />;
        }
    };

    const getDifficultyColor = (difficulty) => {
        switch (difficulty) {
            case 'easy': return 'from-green-500 to-emerald-600 border-green-300 text-green-700';
            case 'medium': return 'from-yellow-500 to-orange-600 border-yellow-300 text-yellow-700';
            case 'hard': return 'from-red-500 to-red-600 border-red-300 text-red-700';
            default: return 'from-gray-500 to-gray-600 border-gray-300 text-gray-700';
        }
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Question Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'question'} />
                <hr className='border-main-border' />

                <div className="p-6">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6 mb-8 border border-indigo-100">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl">
                                <MdUploadFile className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-800">Upload Questions</h2>
                                <p className="text-slate-600">Import questions from CSV files to expand your quiz database</p>
                            </div>
                        </div>
                    </div>

                    {/* Instructions Card */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
                                <FaInfoCircle className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800">CSV Format Instructions</h3>
                        </div>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                            <p className="text-slate-700 mb-3 font-medium">Your CSV file should contain the following columns:</p>
                            <div className="bg-white rounded-lg p-4 font-mono text-sm border border-blue-300">
                                <span className="text-blue-600">No</span>, 
                                <span className="text-green-600">Q</span>, 
                                <span className="text-orange-600">A</span>, 
                                <span className="text-purple-600">B</span>, 
                                <span className="text-pink-600">C</span>, 
                                <span className="text-red-600">D</span>, 
                                <span className="text-indigo-600">Ans</span>
                            </div>
                            <div className="mt-3 text-sm text-slate-600">
                                <p><strong>No:</strong> Question number, <strong>Q:</strong> Question text, <strong>A-D:</strong> Answer options, <strong>Ans:</strong> Correct answer (A, B, C, or D)</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Upload Form */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                        <form onSubmit={handleSubmit} className="max-w-3xl mx-auto space-y-8">
                            {/* Question Type Selection */}
                            <div>
                                <label className="text-lg font-bold mb-4 text-slate-800 flex items-center gap-2">
                                    <MdCategory className="w-6 h-6 text-indigo-600" />
                                    Question Difficulty Level
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {['easy', 'medium', 'hard'].map((difficulty) => (
                                        <button
                                            key={difficulty}
                                            type="button"
                                            onClick={() => setType(difficulty)}
                                            disabled={loading}
                                            className={`p-6 rounded-xl border-2 transition-all duration-300 flex flex-col items-center gap-3 ${
                                                type === difficulty 
                                                    ? `bg-gradient-to-r ${getDifficultyColor(difficulty)} border-current shadow-lg transform scale-105`
                                                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                                            }`}
                                        >
                                            <div className={`p-3 rounded-xl ${
                                                type === difficulty 
                                                    ? 'bg-white/20 text-current'
                                                    : 'bg-gray-200 text-gray-500'
                                            }`}>
                                                {getDifficultyIcon(difficulty)}
                                            </div>
                                            <span className="font-bold capitalize text-lg">{difficulty}</span>
                                            <span className="text-sm opacity-80">
                                                {difficulty === 'easy' && 'Basic knowledge questions'}
                                                {difficulty === 'medium' && 'Moderate difficulty level'}
                                                {difficulty === 'hard' && 'Challenging questions'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* File Upload Area */}
                            <div>
                                <label className="text-lg font-bold mb-4 text-slate-800 flex items-center gap-2">
                                    <FaCloudUploadAlt className="w-6 h-6 text-indigo-600" />
                                    Upload CSV File
                                </label>
                                
                                <div 
                                    className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
                                        dragOver 
                                            ? 'border-indigo-500 bg-indigo-50' 
                                            : file 
                                                ? 'border-green-400 bg-green-50'
                                                : 'border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50'
                                    }`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                >
                                    <input
                                        type="file"
                                        id="file"
                                        accept=".csv"
                                        onChange={handleInputChange}
                                        className="hidden"
                                        disabled={loading}
                                    />
                                    
                                    {!file ? (
                                        <label htmlFor="file" className="cursor-pointer flex flex-col items-center space-y-4">
                                            <div className="p-4 bg-indigo-100 rounded-full">
                                                <FaCloudUploadAlt className="w-12 h-12 text-indigo-600" />
                                            </div>
                                            <div>
                                                <p className="text-xl font-semibold text-slate-700 mb-2">
                                                    {dragOver ? 'Drop your CSV file here' : 'Click to upload or drag and drop'}
                                                </p>
                                                <p className="text-slate-500">Maximum file size: 5MB</p>
                                            </div>
                                            <div className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors">
                                                <FaUpload className="w-4 h-4" />
                                                <span className="font-semibold">Browse Files</span>
                                            </div>
                                        </label>
                                    ) : (
                                        <div className="w-full">
                                            <div className="flex items-center justify-center mb-6">
                                                <div className="p-4 bg-green-100 rounded-full">
                                                    <FaCheckCircle className="w-12 h-12 text-green-600" />
                                                </div>
                                            </div>
                                            
                                            <div className="bg-white rounded-xl p-6 border border-green-200 mb-4">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <FaFileCsv className="w-8 h-8 text-green-600" />
                                                        <div className="text-left">
                                                            <p className="font-bold text-slate-800 text-lg">{file.name}</p>
                                                            <p className="text-slate-500">{(file.size / 1024).toFixed(2)} KB</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={removeFile}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        disabled={loading}
                                                    >
                                                        <FaTimes className="w-5 h-5" />
                                                    </button>
                                                </div>
                                                
                                                {previewUrl && (
                                                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <HiDocumentText className="w-5 h-5 text-slate-600" />
                                                            <span className="font-semibold text-slate-700">File Preview:</span>
                                                        </div>
                                                        <div className="font-mono text-sm bg-white p-3 rounded border border-gray-300 overflow-x-auto">
                                                            {previewUrl.split('\n').slice(0, 3).map((line, index) => (
                                                                <div key={index} className="whitespace-nowrap mb-1">
                                                                    <span className="text-blue-600 mr-2">{index + 1}:</span>
                                                                    <span className="text-slate-700">{line}</span>
                                                                </div>
                                                            ))}
                                                            {previewUrl.split('\n').length > 3 && (
                                                                <div className="text-slate-500 text-xs mt-2">
                                                                    ... and {previewUrl.split('\n').length - 3} more lines
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Upload Progress */}
                            {loading && (
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <FaSpinner className="animate-spin w-6 h-6 text-indigo-600" />
                                            <span className="font-semibold text-slate-800">Uploading Questions...</span>
                                        </div>
                                        <span className="font-bold text-indigo-600">{uploadProgress}%</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-3">
                                        <div
                                            className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500"
                                            style={{ width: `${uploadProgress}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}

                            {/* Submit Button */}
                            <div className="flex justify-center">
                                <button
                                    type="submit"
                                    className={`px-12 py-4 text-lg font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-300 flex items-center gap-3 shadow-lg hover:shadow-xl min-w-[200px] justify-center ${
                                        loading || !file || !type ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                                    }`}
                                    disabled={loading || !file || !type}
                                >
                                    {loading ? (
                                        <>
                                            <FaSpinner className="animate-spin w-5 h-5" />
                                            <span>Uploading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <MdCloudUpload className="w-6 h-6" />
                                            <span>Upload Questions</span>
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Requirements Summary */}
                            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                                <div className="flex items-center gap-3 mb-4">
                                    <FaExclamationTriangle className="w-6 h-6 text-amber-600" />
                                    <span className="font-bold text-amber-800">Upload Requirements</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        <IoCheckmarkCircle className="w-4 h-4 text-green-600" />
                                        <span className="text-slate-700">CSV format only</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <IoCheckmarkCircle className="w-4 h-4 text-green-600" />
                                        <span className="text-slate-700">Maximum 5MB file size</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <IoCheckmarkCircle className="w-4 h-4 text-green-600" />
                                        <span className="text-slate-700">7 columns required</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <IoCheckmarkCircle className="w-4 h-4 text-green-600" />
                                        <span className="text-slate-700">Select difficulty level</span>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Question;